import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";
import { Pagination, PagingParams } from "../models/pagination";
import { DropdownItemProps } from "semantic-ui-react";
import { AccountsReceivableIndexItemDto, ExpandedARIndexItemTicketDto, FilterByARIndex, QueueStatusDto } from "../models/accountsReceivable";

export default class AccountsReceivableIndexStore {
  loadingInitial = false;
  loadingTickets = false;
  loadingAccountsReceivableStatements = false;
  initiatePollingQueue = false;
  loadingQueueStatus = false;
  retryCount: number = 0;
  activeTab: number = 0;
  numberOfPrintAllARStatements: number = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setLoadingInitial = (state: boolean) => {
    this.loadingInitial = state;
  };
  setLoadingAccountsReceivableStatement = (state: boolean) => {
    this.loadingAccountsReceivableStatements = state;
  };
  setLoadingQueueStatus = (state: boolean) => {
    this.loadingQueueStatus = state;
  };
  setRetryCount = (state: number) => {
    this.retryCount = state;
  }
  setNumberOfPrintAllARStatements = (state: number) => {
    this.numberOfPrintAllARStatements = state;
  }
  get canRetry() {
    return (this.retryCount !== 3)
  }
  get cantPrint() {
    return (this.numberOfPrintAllARStatements > 5)
  }
  setActiveTab = (activeTab: string | number | undefined) => {
    this.activeTab = activeTab ? Number(activeTab) : 0;
  };
  setInitiatePollingQueue = (state: boolean) => {
    this.initiatePollingQueue = state;
  }

  locationsLoading: boolean = false;
  inventoryStockLocationList: DropdownItemProps[] = [];
  accountsReceivableIndexPagingParams = new PagingParams(1, 25);
  expandedARTicketsPagingParams = new PagingParams();

  accountsReceivableIndexPredicate = new Map().set('filterBy', FilterByARIndex.All);
  accountsReceivableIndexPagination: Pagination | null = null;
  expandedARTicketsPagination: Pagination | null = null;
  
  expandedARTicketsPredicate = new Map();
  expandedARIndexItemTickets = new Map<string | undefined, ExpandedARIndexItemTicketDto>();
  expandedARIndexItem: AccountsReceivableIndexItemDto | undefined = undefined;
  
  accountsReceivableIndexRegistry = new Map<string | undefined, AccountsReceivableIndexItemDto>();

  printAllStatementInitiated = false;
  setPrintAllStatementInitiated = (state: boolean) => {
    this.printAllStatementInitiated = state;
  }
  printAllStatementQueueStatus: QueueStatusDto | undefined = undefined;
  setPrintAllStatementQueueStatus = (value: QueueStatusDto | undefined) => {
    this.printAllStatementQueueStatus = value;
  };
  // Accounts Receivable Index

  printAllAccountsReceivableStatements = async () => {
    this.setLoadingAccountsReceivableStatement(true);
    try {
      await agent.printAccountsReceivable.printAllAccountsReceivableStatement();
      runInAction(() => {
        if(this.initiatePollingQueue)
          this.setRetryCount(this.retryCount + 1);
        else
          this.setRetryCount(0);

        this.setNumberOfPrintAllARStatements(this.numberOfPrintAllARStatements + 1);      
        this.printAllStatementInitiated = true;
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.setLoadingAccountsReceivableStatement(false);
    }
  }
  queuePrintAllAccountsReceivableStatement = async () => {
    this.setLoadingQueueStatus(true);
    try {
      const queueStatus = await agent.printAccountsReceivable.queuePrintAllAccountsReceivableStatement();
      runInAction(() => {
        this.printAllStatementQueueStatus = queueStatus;
      });
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.setLoadingQueueStatus(false);
      });
      return this.printAllStatementQueueStatus;
    }
  }

  resetARIndexPredicate = () => {
    this.accountsReceivableIndexPredicate.clear();
  }
  setARIndexPredicate = (predicate: string, value?: string) => {
    if (value)
      this.accountsReceivableIndexPredicate.set(predicate, value);
    else
      this.accountsReceivableIndexPredicate.delete(predicate);
  }
  setARIndexPagination = (pagination: Pagination | null) => {
    this.accountsReceivableIndexPagination = pagination;
  }


  get arIndexAxiosParams() {
    const params = new URLSearchParams();
    params.append("pageNumber", this.accountsReceivableIndexPagingParams.pageNumber.toString());
    params.append("pageSize", this.accountsReceivableIndexPagingParams.pageSize.toString());
    this.accountsReceivableIndexPredicate.forEach((value, key) => params.append(key, value));

    return params;
  }

  get arTicketsAxiosParams() {
    const params = new URLSearchParams();

    params.append("pageNumber", this.expandedARTicketsPagingParams.pageNumber.toString());
    params.append("pageSize", this.expandedARTicketsPagingParams.pageSize.toString());
    params.append("filterBy", this.accountsReceivableIndexPredicate.get("filterBy") ?? FilterByARIndex.All);
    
    return params;
  }

  setARIndexItem = (arItem: AccountsReceivableIndexItemDto) => {
    this.accountsReceivableIndexRegistry.set(arItem.id, arItem);
  }
  get getAccountsReceivableIndex() {
    return Array.from(this.accountsReceivableIndexRegistry.values())
  }

  // Expanding an item on the inventory index
  setExpandedARItemTicketsPagination = (pagination: Pagination | null) => {
    this.expandedARTicketsPagination = pagination;
  }
  setExpandedARIndexItem = (values: AccountsReceivableIndexItemDto) => {
    this.expandedARIndexItem = values;
  };

  resetExpandedTickets = () => {
    this.expandedARIndexItemTickets.clear();
  };
  setExpandedTickets = (id: string, values: ExpandedARIndexItemTicketDto) => {
    this.expandedARIndexItemTickets.set(id, values);
  };
  setExpandedTicketsPredicate = (predicate: string, value: string | undefined | Date) => {
    if(value) this.expandedARTicketsPredicate.set(predicate, value);
    else this.expandedARTicketsPredicate.delete(predicate);
  }
  get getExpandedARIndexItemTickets() {
    return Array.from(this.expandedARIndexItemTickets.values())
  }

  loadARIndex = async () => {
    this.loadingInitial = true;
    this.accountsReceivableIndexRegistry.clear();
    try {
      const result = await agent.accountsReceivableIndex.getAccountsReceivableIndex(this.arIndexAxiosParams);
      runInAction(() => {
        result.data.forEach(dataItem => this.setARIndexItem(dataItem))
      });
      this.setARIndexPagination(result.pagination);
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };

  loadExpandedARIndexItemTickets = async (id: string) => {
    this.loadingTickets = true;
    this.resetExpandedTickets();
    try {
      const invItmTicketsResult = await agent.accountsReceivableIndex.getExpandedARIndexTickets(id, this.arTicketsAxiosParams);
      runInAction(() => {
        invItmTicketsResult.data.forEach(dataItem => this.setExpandedTickets(dataItem.ticketId, dataItem));
      });
      this.setExpandedARItemTicketsPagination(invItmTicketsResult.pagination);
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingTickets = false;
      });
    }
  }

  clearAccountsReceivableIndexFilters = () => {
    this.accountsReceivableIndexPredicate.clear();
  }
  resetAccountsReceivableIndexRegistry = () => {
    this.accountsReceivableIndexRegistry.clear();
  }
  resetExpandedTicketsRegistry = () => {
    this.expandedARIndexItemTickets.clear();
  }

  setAccountsReceivableIndexPagingParams = (pagingParams: PagingParams) => {
    this.accountsReceivableIndexPagingParams = pagingParams;
  }

  setExpandedTicketsPagingParams = (pagingParams: PagingParams) => {
    this.expandedARTicketsPagingParams = pagingParams;
  }
}
